// Use lodash.cloneDeep because we want deep copy, not shallow copy
import cloneDeep from 'lodash/fp/cloneDeep';
import { AVRProducts } from '@/utils/enums';
import tradeinService from '../services/tradeinService';
import TradeInSource from '@/utils/enums/tradeInSource';

const initialTradeInVehicle = {
  year: '',
  make: '',
  model: '',
  trim: '',
  style: '',
  mileage: null,
  engine: null,
  drivetrain: null,
  transmission: '',
  bodyStyle: null,
  boxLength: '',
  fuelType: '',
  type: '',
  additionalDetails: '',
  balanceOwing: null,
  tradeEquityUsedForCIM: false,
  vehicleCategoryType: '',
  vin: '',
  additionalVehicleAttributes: {
    colour: '',
    condition: '',
    beenInAccident: '',
    needsBodyWork: '',
    hasMechanicalIssues: '',
  },
};

const initialState = {
  updateLeadVehicles: {
    tradein: cloneDeep(initialTradeInVehicle),
    acquire: cloneDeep(initialTradeInVehicle),
  },
  // trade/acquire vehicle
  vehicle: cloneDeep(initialTradeInVehicle),
  vehicleOptions: {
    years: [],
    makes: [],
    models: [],
    trims: [],
    styles: [],
    engine: [],
  },
  tradein: {
    deductions: {
      advertising: null,
      aged_discount: null,
      dealer_profit: null,
      overhead: null,
      reconditioning: null,
    },
    low_list_price: null,
    high_list_price: null,
    tax_rate: null,
    trade_in_value: {
      low_value: null,
      high_value: null,
    },
    vehicle_count: null,
    tradeVehicleOutOfRange: false,
  },
  acquire: {
    deductions: {
      aged_discount: null,
      reconditioning: null,
      advertising: null,
      overhead: null,
      dealer_profit: null,
    },
    trade_in_value: {
      low_value: null,
      high_value: null,
    },
    high_list_price: null,
    low_list_price: null,
    vehicle_count: null,
    tax_rate: null,
    buy_value: {
      low_value: null,
      high_value: null,
    },
  },
  hasBalanceOnVehicle: null,
  hasTradeIn: false,
  tradeInSource: null,
};
const getters = {
  getYear: state => state.vehicle.year,
  getYearOptions: state => state.vehicleOptions.years,
  getMake: state => state.vehicle.make,
  getMakeOptions: state => state.vehicleOptions.makes,
  getModel: state => state.vehicle.model,
  getModelOptions: state => state.vehicleOptions.models,
  getTrim: state => state.vehicle.trim,
  getTrimOptions: state => state.vehicleOptions.trims,
  getStyle: state => state.vehicle.style,
  getStyleOptions: state => state.vehicleOptions.styles,
  getVehicleName: state => {
    const { year, make, model, trim, style } = state.vehicle;
    if (!year) return '';

    let vehicleName = `${year} ${make} ${model}`;
    if (trim && trim !== 'no-trim') vehicleName += ` ${trim}`;
    if (style) vehicleName += ` ${style}`;

    return vehicleName;
  },
  getBaseVehicleObject: state => ({
    year: state.vehicle.year,
    make: state.vehicle.make,
    model: state.vehicle.model,
    trim: state.vehicle.trim,
    style: state.vehicle.style,
  }),
  getHasTradeInValuation: state =>
    state.tradein.trade_in_value.low_value !== null && state.tradein.trade_in_value.high_value !== null,
  getEngineOptions: state => state.vehicleOptions.engine,
  getEngine: state => state.vehicle.engine,
  getDrivetrain: state => state.vehicle.drivetrain,
  getBodyStyle: state => state.vehicle.bodyStyle,
  getTransmission: state => state.vehicle.transmission,
  getBoxLength: state => state.vehicle.boxLength,
  getFuelType: state => state.vehicle.fuelType,
};
const mutations = {
  SET_YEAR_OPTIONS(state, data) {
    state.vehicleOptions.years = data;
  },
  SET_VEHICLE_YEAR(state, data) {
    state.vehicle.year = data;
  },
  SET_MAKE_OPTIONS(state, data) {
    state.vehicleOptions.makes = data;
  },
  SET_VEHICLE_MAKE(state, data) {
    state.vehicle.make = data;
  },
  SET_MODEL_OPTIONS(state, data) {
    state.vehicleOptions.models = data;
  },
  SET_VEHICLE_MODEL(state, data) {
    state.vehicle.model = data;
  },
  SET_TRIM_OPTIONS(state, data) {
    state.vehicleOptions.trims = data;
  },
  SET_VEHICLE_TRIM(state, data) {
    state.vehicle.trim = data;
  },
  SET_STYLE_OPTIONS(state, data) {
    state.vehicleOptions.styles = data;
  },
  SET_VEHICLE_STYLE(state, data) {
    state.vehicle.style = data;
  },
  SET_VEHICLE_MILEAGE(state, data) {
    state.vehicle.mileage = data;
  },
  SET_ENGINE_OPTIONS(state, data) {
    state.vehicleOptions.engine = data;
  },
  SET_VEHICLE_ENGINE(state, data) {
    state.vehicle.engine = data;
  },
  SET_VEHICLE_DRIVETRAIN(state, data) {
    state.vehicle.drivetrain = data;
  },
  SET_VEHICLE_BODYSTYLE(state, data) {
    state.vehicle.bodyStyle = data;
  },
  SET_VEHICLE_BOXLENGTH(state, data) {
    state.vehicle.boxLength = data;
  },
  SET_VEHICLE_FUELTYPE(state, data) {
    state.vehicle.fuelType = data;
  },
  SET_VEHICLE_TRANSMISSION(state, data) {
    state.vehicle.transmission = data;
  },
  SET_VEHICLE_BALANCE_OWING(state, data) {
    state.vehicle.balanceOwing = data;
  },
  SET_TRADE_EQUITY_USED_FOR_CIM(state, data) {
    state.vehicle.tradeEquityUsedForCIM = data;
  },
  SET_TRADE_VEHICLE_CATEGORY_TYPE(state, data) {
    state.vehicle.vehicleCategoryType = data;
  },
  SET_HAS_TRADE_IN(state, data) {
    state.hasTradeIn = data;
  },
  SET_HAS_BALANCE_ON_VEHICLE(state, data) {
    state.hasBalanceOnVehicle = data;
  },
  RESET_VEHICLE(state) {
    state.vehicle = cloneDeep(initialTradeInVehicle);
  },
  SET_VEHICLE(state, { year, make, model, trim, style }) {
    state.vehicle.year = year;
    state.vehicle.make = make;
    state.vehicle.model = model;
    state.vehicle.trim = trim;
    state.vehicle.style = style;
  },
  SET_TRADEIN_REPORT(state, data) {
    state.tradein = data;
  },
  SET_ACQUIRE_REPORT(state, data) {
    state.acquire = data;
  },
  SET_LEAD_TRADEIN_VEHICLE(state) {
    state.updateLeadVehicles.tradein = cloneDeep(state.vehicle);
  },
  SET_LEAD_ACQUIRE_VEHICLE(state) {
    state.updateLeadVehicles.acquire = cloneDeep(state.vehicle);
  },
  SET_VEHICLE_TYPE(state, data) {
    state.vehicle.type = data;
  },
  SET_VEHICLE_ADDITIONAL_DETAILS(state, data) {
    state.vehicle.additionalDetails = data;
  },
  SET_VEHICLE_VIN(state, data) {
    state.vehicle.vin = data;
  },
  SET_VEHICLE_ADDITIONAL_VEHICLE_ATTRIBUTES(state, data) {
    state.vehicle.additionalVehicleAttributes = data;
  },
  SET_TRADEIN_SOURCE(state, data) {
    state.tradeInSource = data;
  },
};
const actions = {
  async getYears({ rootState, commit }) {
    commit('SET_YEAR_OPTIONS', []);
    const apiKey = rootState.route.query.key;
    const externalId = rootState.route.query.external_id;
    const { tradeInSource } = rootState.business.info;
    const getYears = tradeInSource === TradeInSource.CARFAX ? tradeinService.getYearsV2 : tradeinService.getYears;
    const res = await getYears(tradeInSource, apiKey, externalId);
    commit('SET_YEAR_OPTIONS', res.data);
  },
  async getMakes({ rootState, state, commit }) {
    commit('SET_MAKE_OPTIONS', []);
    const apiKey = rootState.route.query.key;
    const externalId = rootState.route.query.external_id;
    const { tradeInSource } = rootState.business.info;
    const getMakes = tradeInSource === TradeInSource.CARFAX ? tradeinService.getMakesV2 : tradeinService.getMakes;
    const res = await getMakes(state.vehicle, tradeInSource, apiKey, externalId);
    commit('SET_MAKE_OPTIONS', res.data);
  },
  async getModels({ rootState, state, commit }) {
    commit('SET_MODEL_OPTIONS', []);
    const apiKey = rootState.route.query.key;
    const externalId = rootState.route.query.external_id;
    const { tradeInSource } = rootState.business.info;
    const getModels = tradeInSource === TradeInSource.CARFAX ? tradeinService.getModelsV2 : tradeinService.getModels;
    const res = await getModels(state.vehicle, tradeInSource, apiKey, externalId);
    commit('SET_MODEL_OPTIONS', res.data);
  },
  async getTrims({ rootState, state, commit }) {
    commit('SET_TRIM_OPTIONS', []);
    const apiKey = rootState.route.query.key;
    const externalId = rootState.route.query.external_id;
    const { tradeInSource } = rootState.business.info;
    const getTrims = tradeInSource === TradeInSource.CARFAX ? tradeinService.getTrimsV2 : tradeinService.getTrims;
    const res = await getTrims(state.vehicle, tradeInSource, apiKey, externalId);
    commit('SET_TRIM_OPTIONS', res.data);
  },
  async getEngine({ rootState, state, commit }) {
    commit('SET_ENGINE_OPTIONS', []);
    const res = await tradeinService.getEngineDetailsV2(
      rootState.route.query.key,
      rootState.route.query.external_id,
      state.vehicle
    );
    commit('SET_ENGINE_OPTIONS', res.data.engineOptions);
  },
  async getStyles({ rootState, state, commit }) {
    commit('SET_STYLE_OPTIONS', []);
    let res;
    if (state.vehicle.trim === 'no-trim') {
      res = await tradeinService.getStylesNoTrim(state.vehicle, rootState.business.info.tradeInSource);
    } else {
      res = await tradeinService.getStyles(state.vehicle, rootState.business.info.tradeInSource);
    }
    commit('SET_STYLE_OPTIONS', res.data);
  },
  // eslint-disable-next-line consistent-return
  async getReport({ rootState, rootGetters, state, commit }) {
    const apiKey = rootState.route.query.key;
    const externalId = rootState.route.query.external_id;
    const reportVehicle = state.vehicle;
    const { tradeInSource } = rootState.business.info;

    const getAcquireReport =
      tradeInSource === TradeInSource.CARFAX ? tradeinService.getAcquireReportV2 : tradeinService.getAcquireReport;
    const getReport = tradeInSource === TradeInSource.CARFAX ? tradeinService.getReportV2 : tradeinService.getReport;
    // Check if the vehicle has no trim
    if (reportVehicle.trim === 'no-trim') {
      reportVehicle.trim = null;
    }
    const payload = {
      vehicle: reportVehicle,
      province: rootState.business.info.integrationSettings.province,
      postalCode: rootState.business.info.integrationSettings.postalCode,
      which: tradeInSource,
    };
    let res;
    try {
      switch (rootGetters.getCurrentProduct) {
        case AVRProducts.ACQUIRE:
          res = await getAcquireReport(payload, apiKey, externalId);
          commit('SET_ACQUIRE_REPORT', res.data);
          break;
        default:
          res = await getReport(payload, apiKey, externalId);
          commit('SET_TRADEIN_REPORT', res.data);
          commit('SET_DHS_STATE', ['trade', 'complete'], { root: true });
      }
      commit('SET_TRADEIN_SOURCE', tradeInSource);
    } catch (err) {
      commit('SET_TRADEIN_SOURCE', null);
      return Promise.reject(err);
    }
  },
  resetVehicleOptions({ commit }, optionsToReset = []) {
    optionsToReset.forEach(option => {
      commit(`SET_${option.toUpperCase()}_OPTIONS`, []);
      commit(`SET_VEHICLE_${option.toUpperCase()}`, '');
    });
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
